import {
  useState,
  useContext,
  useEffect,
  lazy,
  useMemo,
  useCallback,
} from "react";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from "react-router-dom";
import ContractRoutes from "./contract/routing/ContractRoutes";
import ContractStateRedirects from "./contract/routing/ContractStateRedirects";
import { AttachmentUrlCacheProvider } from "./contract/attachments/FileItem";
import { ContractProvider } from "./contract/ContractContext";
import ContractUpdatesProvider from "./contract/updates/ContractUpdatesProvider";
import ContractUpdateNotificationProvider from "./contract/updates/ContractUpdateNotificationProvider";
import { InputValidationProvider } from "./contract/validation/InputValidationContext";
import { ContractValidationProvider } from "./contract/validation/ContractValidation";
import { ContractValidationResultProvider } from "./contract/validation/ContractValidationResultContext";
import { DocumentPreviewProvider } from "./contract/preview/DocumentPreview";
import { LoadContract, useLoadContractContext } from "./LoadContract";
import Main from "./Main";
import { Location } from "history";
import {
  extractContractParams,
  useContractParamsContext,
} from "./contract/ContractParams";
import { getUrl, useContractUrls } from "./contract/routing/ContractUrls";
import { PageViewContext } from "./contract/routing/TrackPageViews";
import { TrackProgress } from "./contract/routing/TrackProgress";
import { ReactNode } from "react";
import { ContractClientProvider } from "./api/ContractClient";
import LandingPage from "./contract/landing/LandingPage";
import NotFoundScreen from "./shared/error/NotFoundScreen";
import StaticErrorScreen from "./shared/error/StaticErrorScreen";
import {
  FlowNoPreviewLoadingScreen,
  StaticLoadingScreen,
} from "./shared/LoadingScreen";
import { CONTRACT_PATH_ROUTES } from "./contract/routing/ContractPath";
import { FaqFlyoutProvider } from "./about/FaqFlyout";

const CreateContractPage = lazy(
  () => import("./contract/create-contract/CreateContractPage")
);
const ArchivedContractScreen = lazy(
  () => import("./shared/error/ArchivedContractScreen")
);

export function ClearContractParams() {
  const location = useLocation();
  const { clear } = useLoadContractContext();
  const { token } = extractContractParams(location.search);
  const [redirect, setRedirect] = useState(false);
  const urls = useContractUrls();

  useEffect(() => {
    if (!token) {
      clear();
    } else {
      setRedirect(true);
    }
  }, [clear, setRedirect, token]);

  if (!redirect) return <StaticLoadingScreen />;

  return <Redirect to={urls.contract} />;
}

export function ParseContractParams() {
  const location = useLocation();
  const { setParams } = useContractParamsContext();
  const params = useMemo(
    () => extractContractParams(location.search),
    [location.search]
  );
  useEffect(() => {
    if (params) setParams(params);
  }, [setParams, params]);
  if (params.token) return <FlowNoPreviewLoadingScreen />;
  return <NotFoundScreen />;
}

export function TrackSalesContractAppRoute({
  children,
}: {
  children: ReactNode;
}) {
  const location = useLocation();
  const pageViewContext = useContext(PageViewContext);

  if (!pageViewContext) throw new Error(`No PageViewContext found!`);

  const { trackLocation } = pageViewContext;

  useEffect(() => {
    const timeout = window.setTimeout(trackLocation, undefined, location);

    return () => clearTimeout(timeout);
  }, [location, trackLocation]);

  return <>{children}</>;
}

function useClearParams() {
  const { clear } = useLoadContractContext();
  const { setParams } = useContractParamsContext();
  useEffect(() => {
    setParams({ token: null });
    clear();
  }, [setParams, clear]);
}

function LandingPageRoute() {
  useClearParams();

  return <LandingPage />;
}

function CreateContractPageRoute() {
  useClearParams();

  return <CreateContractPage />;
}

function useUpdateContractParamsFromQuery() {
  const history = useHistory();
  const { token, setParams } = useContractParamsContext();
  const updateParams = useCallback(
    function (x: Location<unknown>) {
      const params = extractContractParams(x.search);
      if (params.token !== token) {
        setParams(params);
      }
    },
    [token, setParams]
  );
  useEffect(() => {
    updateParams(history.location);
    return history.listen(updateParams);
  }, [history, updateParams]);
}

function LoadedContractSwitch() {
  const { token } = useContractParamsContext();
  const { loadError, contract } = useLoadContractContext();
  useUpdateContractParamsFromQuery();

  if (token && (loadError || contract)) {
    return (
      <Switch>
        <Route path="/t/:token/:path*" component={ShortUrlRoute} />
        <Route>
          {() => (
            <TrackSalesContractAppRoute>
              {loadError ? (
                <StaticErrorScreen />
              ) : !contract?.data ? (
                <NotFoundScreen />
              ) : (
                <Main>
                  <ContractClientProvider>
                    <ContractValidationResultProvider>
                      <InputValidationProvider>
                        <ContractProvider>
                          <ContractValidationProvider>
                            <DocumentPreviewProvider>
                              <AttachmentUrlCacheProvider>
                                <ContractUpdatesProvider>
                                  <FaqFlyoutProvider>
                                    <ContractStateRedirects>
                                      <TrackProgress />
                                    </ContractStateRedirects>
                                    <ContractRoutes />
                                  </FaqFlyoutProvider>
                                </ContractUpdatesProvider>
                              </AttachmentUrlCacheProvider>
                            </DocumentPreviewProvider>
                          </ContractValidationProvider>
                        </ContractProvider>
                      </InputValidationProvider>
                    </ContractValidationResultProvider>
                  </ContractClientProvider>
                </Main>
              )}
            </TrackSalesContractAppRoute>
          )}
        </Route>
      </Switch>
    );
  }

  return (
    <Switch>
      <Route path="/t/:token/:path*" component={ShortUrlRoute} />
      <Route path="/" exact component={LandingPageRoute} />
      <Route path="/opprett" exact component={CreateContractPageRoute} />
      <Route path="/arkiv" exact component={ArchivedContractScreen} />
      <Route component={NotFoundScreen} />
    </Switch>
  );
}

function ShortUrlRoute() {
  const match = useRouteMatch<{ token: string; path?: string }>();
  const { token, path } = match.params;
  const url = token
    ? getUrl(path ? `/${path}` : CONTRACT_PATH_ROUTES.contract.path, { token })
    : "/";
  const context = useContractParamsContext();
  useEffect(() => {
    if (context.token !== token) {
      context.setParams({ token });
    }
  }, [token, context]);
  return <Redirect to={url} />;
}

export default function SalesContractApp() {
  return (
    <ContractUpdateNotificationProvider>
      <LoadContract>
        <LoadedContractSwitch />
      </LoadContract>
    </ContractUpdateNotificationProvider>
  );
}
